'use client';

import CustomLoading from '@/components/custom-loading';
import { pageDirectories, pageDirectoryParameter } from '@/constant/route';
import { generateSsoRedirectToClient } from '@/server/api/v1/auth/auth.action';
import { AuthErrorCode } from '@/server/config/auth.error';
import { LoginMethod } from '@/types/enum';
import { useTranslations } from 'next-intl';
import { memo, useCallback, useEffect, useRef } from 'react';

const page_path = pageDirectories['Signin'];
const key_subject = pageDirectoryParameter[page_path].subject!;
const key_code = pageDirectoryParameter[page_path].code!;
const key_timestamp = pageDirectoryParameter[page_path].timestamp!;

/**
 * Bộ ngắt-loop cho luồng force-SSO. Mỗi lần vào `/signin?sso=auto` là một lần auto-redirect.
 * Nếu đăng nhập thất bại mà session không thiết lập được, middleware sẽ đẩy về `?sso=auto`
 * lặp lại — đếm số lần trong một cửa sổ ngắn, vượt ngưỡng thì DỪNG auto và đưa người dùng
 * về form đăng nhập thủ công kèm thông báo, thay vì lặp vô hạn.
 */
const ATTEMPT_KEY = 'sso-auto-attempts';
const ATTEMPT_WINDOW_MS = 60_000;
const MAX_ATTEMPTS = 3;

type AttemptState = { count: number; firstAt: number };

function readAttempts(): AttemptState {
    try {
        const raw = sessionStorage.getItem(ATTEMPT_KEY);
        if (!raw) return { count: 0, firstAt: Date.now() };
        const parsed = JSON.parse(raw) as AttemptState;
        if (
            typeof parsed?.count !== 'number' ||
            typeof parsed?.firstAt !== 'number'
        ) {
            return { count: 0, firstAt: Date.now() };
        }
        return parsed;
    } catch {
        return { count: 0, firstAt: Date.now() };
    }
}

/**
 * Ghi nhận một lần thử auto-redirect, trả về `true` nếu ĐÃ vượt ngưỡng (nên dừng).
 * Cửa sổ hết hạn thì reset bộ đếm. Storage bị chặn thì luôn cho đi tiếp (không chặn đăng nhập).
 */
function registerAttemptAndShouldStop(): boolean {
    try {
        const now = Date.now();
        const prev = readAttempts();
        const inWindow = now - prev.firstAt <= ATTEMPT_WINDOW_MS;
        const next: AttemptState = inWindow
            ? { count: prev.count + 1, firstAt: prev.firstAt }
            : { count: 1, firstAt: now };
        sessionStorage.setItem(ATTEMPT_KEY, JSON.stringify(next));
        return next.count > MAX_ATTEMPTS;
    } catch {
        return false;
    }
}

function clearAttempts() {
    try {
        sessionStorage.removeItem(ATTEMPT_KEY);
    } catch {
        // ignore
    }
}

/** Dựng path form đăng nhập thủ công kèm mã lỗi (không có sso=auto nên không tự đẩy lại). */
function buildManualSigninPath(): string {
    const params = new URLSearchParams();
    params.set(key_timestamp, Date.now().toString());
    params.set(key_code, AuthErrorCode.SSO_Unavailable);
    params.set(key_subject, LoginMethod.SSO);
    return `${pageDirectories['Signin']}?${params.toString()}`;
}

/**
 * Tự động đẩy người dùng sang trang đăng nhập SSO (Keycloak) ngay khi vào /signin?sso=auto.
 *
 * `callbackUrl` (path/URL gốc user định truy cập) được nhét vào tham số `state` của SSO để
 * trang callback (/signin/sso) điều hướng trả về đúng chỗ sau khi đăng nhập thành công.
 */
export const SsoAutoRedirect = memo(
    ({ callbackUrl }: { callbackUrl?: string }) => {
        const transPage = useTranslations('SignIn');
        const startedRef = useRef(false);

        const onRedirect = useCallback(async () => {
            if (startedRef.current) return;
            startedRef.current = true;

            // Quá nhiều vòng auto-redirect trong thời gian ngắn = đang loop mà không vào được.
            // Dừng tự động, đưa về form thủ công (nút "Đăng nhập SSO" dùng luồng riêng, không
            // tính vào bộ đếm này nên người dùng vẫn thử lại được).
            if (registerAttemptAndShouldStop()) {
                clearAttempts();
                window.location.replace(buildManualSigninPath());
                return;
            }

            const result = await generateSsoRedirectToClient({
                state: callbackUrl ?? '',
            });

            if (result?.data) {
                window.location.href = result.data;
            } else {
                // Không tạo được URL SSO -> quay về form đăng nhập thường (bỏ cờ sso=auto)
                window.location.replace(pageDirectories['Signin']);
            }
        }, [callbackUrl]);

        useEffect(() => {
            onRedirect();
        }, [onRedirect]);

        return (
            <div className='flex items-center justify-center'>
                <CustomLoading.Dot className='text-sm font-medium text-muted-foreground'>
                    {transPage('RedirectingToSso')}
                </CustomLoading.Dot>
            </div>
        );
    }
);

SsoAutoRedirect.displayName = 'SsoAutoRedirect';
